/**
 * Renders comment content as sanitized markdown with @mention highlighting.
 * Word Add-in variant — compact styling for narrow task pane.
 *
 * Supports: bold, italic, strikethrough, lists, links, inline code.
 * No images or raw HTML allowed.
 */

import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { NegotiationParty } from '../../services/negotiationsApi'

const MENTION_REGEX = /@(?:"([^"]+)"|(\S+))/g

interface MarkdownContentProps {
  content: string
  mentions?: Array<{ koblink_id: string; display_name: string }>
  parties: NegotiationParty[]
  resolved?: boolean
}

function buildMentionMap(
  mentions: Array<{ koblink_id: string; display_name: string }> | undefined,
  parties: NegotiationParty[],
): Map<string, string> {
  const map = new Map<string, string>()
  if (!mentions) return map
  for (const m of mentions) {
    const party = parties.find(p => p.actor?.koblink_id === m.koblink_id)
    const color = party?.color || '#6b7280'
    if (m.display_name) map.set(m.display_name.toLowerCase(), color)
    if (party?.tag_name) map.set(party.tag_name.toLowerCase(), color)
  }
  return map
}

function segmentMentions(
  text: string,
  mentionMap: Map<string, string>,
): React.ReactNode[] {
  if (mentionMap.size === 0) return [text]
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  MENTION_REGEX.lastIndex = 0
  let match
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    const name = match[1] || match[2]
    const color = mentionMap.get(name.toLowerCase())
    if (color) {
      parts.push(
        <span
          key={match.index}
          className="font-semibold px-0.5 rounded"
          style={{ backgroundColor: `${color}40` }}
        >
          @{name}
        </span>
      )
    } else {
      parts.push(match[0])
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts
}

function processChildren(
  children: React.ReactNode,
  mentionMap: Map<string, string>,
): React.ReactNode {
  if (mentionMap.size === 0) return children
  if (typeof children === 'string') {
    return segmentMentions(children, mentionMap)
  }
  if (Array.isArray(children)) {
    return children.map((child, i) =>
      typeof child === 'string'
        ? <React.Fragment key={i}>{segmentMentions(child, mentionMap)}</React.Fragment>
        : child
    )
  }
  return children
}

export default function MarkdownContent({
  content,
  mentions,
  parties,
  resolved = false,
}: MarkdownContentProps) {
  const mentionMap = buildMentionMap(mentions, parties)

  return (
    <div className={`text-[11px] leading-relaxed ${resolved ? 'line-through' : ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        allowedElements={[
          'p', 'strong', 'em', 'del', 'a', 'ul', 'ol', 'li', 'code', 'br',
        ]}
        unwrapDisallowed
        components={{
          p: ({ children }) => (
            <p className="mb-0 last:mb-0">{processChildren(children, mentionMap)}</p>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white/90 hover:text-white"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="px-0.5 py-px rounded bg-black/20 text-[10px] font-mono">
              {children}
            </code>
          ),
          ul: ({ children }) => <ul className="list-disc ml-3 my-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal ml-3 my-0.5">{children}</ol>,
          li: ({ children }) => (
            <li className="my-0">{processChildren(children, mentionMap)}</li>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
